import React, { FC, useCallback, useMemo, useState } from 'react'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { AsH1 } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { PhaseSlug, SweepstakeObject } from '@/types/codegen-federation'
import { useSafeAnalytics } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import { GiveawayVideoModal } from '@/views/PreSales/PreSalesGiveawayView/components/GiveawayVideoModal'
import { GiveawayLogo } from '@/views/PreSales/PreSalesGiveawayView/components/Hero/GiveawayLogo'
import { GiveawayPlayButton } from '@/views/PreSales/PreSalesGiveawayView/components/Hero/GiveawayPlayButton'
import {
  GButtonMD,
  GEyebrowMD,
  GResponsiveText,
  GTitleMD,
  GTitleXL,
} from '@/views/PreSales/PreSalesGiveawayView/components/Typography'
import { useGiveawaySchedule } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawaySchedule'
import { GiveawayPromoBanner } from './GiveawayPromoBanner'

interface Props {
  onScrollToProducts: () => void
  trailerId?: string | null | undefined
  title: string
  logoUrl: string
  togglePauseBackgroundVideo: (value?: boolean | undefined) => void
  winnerAnnouncedDate?: SweepstakeObject['winnerAnnouncedDate']
  projectName: SweepstakeObject['projectName']
  theatricalSlug: string
  inTheatersText: string
  videoButtonText: string
  phase: PhaseSlug | null
  activateTrailerExperiment?: () => void
}
const GiveawayHero: FC<Props> = ({
  onScrollToProducts,
  title,
  trailerId,
  logoUrl,
  togglePauseBackgroundVideo,
  winnerAnnouncedDate,
  theatricalSlug,
  inTheatersText,
  phase,
  videoButtonText,
  projectName,
  activateTrailerExperiment,
}) => {
  const [isTrailerModalOpen, setIsTrailerModalOpen] = useState(false)
  const { t } = useTranslate('tickets')
  const { push } = useRouter()
  const { isActive } = useGiveawaySchedule(theatricalSlug)
  const { track } = useSafeAnalytics()

  const showInTheatersBadge = inTheatersText && phase !== 'guild_access'

  const handleOpenTrailer = useCallback(() => {
    setIsTrailerModalOpen(true)
    togglePauseBackgroundVideo(true)
    if (theatricalSlug === slugs.theLastRodeo && activateTrailerExperiment) activateTrailerExperiment()
    track('Giveaway Video Clicked')
  }, [activateTrailerExperiment, theatricalSlug, togglePauseBackgroundVideo, track])

  const handleCloseTrailer = useCallback(() => {
    setIsTrailerModalOpen(false)
    togglePauseBackgroundVideo(false)
  }, [togglePauseBackgroundVideo])

  const { heroTitle, btnText, onClick } = useMemo(() => {
    if (!isActive && phase === 'theatrical') {
      return {
        heroTitle: t('projectNowInTheaters', '{{projectName}} now in theaters', { projectName }),
        btnText: t('getShowtimesGiveaway', 'Get Showtimes'),
        onClick: () => push(`/tickets/${theatricalSlug}`),
      }
    }

    if (!isActive && phase === 'guild_access') {
      return {
        heroTitle: t('watchProjectNow', 'Watch {{projectName}} Now', { projectName }),
        btnText: t('watchNowGiveaway', 'Watch Now'),
        onClick: () => push(`/movies/${theatricalSlug}`),
      }
    }

    return { heroTitle: title, btnText: t('preOrderTicets', 'Pre-order Tickets'), onClick: onScrollToProducts }
  }, [isActive, onScrollToProducts, phase, projectName, push, t, theatricalSlug, title])

  return (
    <div className="flex flex-1 flex-col">
      <div className="relative m-auto flex w-full max-w-[1080px] flex-1 flex-col gap-4 pt-4 md:flex-row md:items-center md:justify-center md:gap-10 md:pt-0">
        <FlexColumn className="gap-4 md:shrink-0">
          <GiveawayLogo theatricalSlug={theatricalSlug} logoUrl={logoUrl} />

          {showInTheatersBadge && <InTheatersPill text={inTheatersText} className="mt-4 hidden md:flex" />}

          <GResponsiveText
            className="text-center md:max-w-[540px] md:text-left"
            as={AsH1}
            color="white"
            MobileTextComponent={GTitleMD}
            DesktopTextComponent={GTitleXL}
          >
            {heroTitle}
          </GResponsiveText>

          <GiveawayPromoBanner theatricalSlug={theatricalSlug} winnerAnnouncedDate={winnerAnnouncedDate} />

          <div>
            <Button
              className="hidden flex-col rounded-[14px] border-none !bg-giveaway-secondary-accent !py-[17px] md:mr-auto md:mt-4 md:flex md:!px-7"
              onClick={onClick}
              variant="core-gray-900"
            >
              <GButtonMD color="white" className="flex items-center gap-2">
                {btnText}
              </GButtonMD>
            </Button>
          </div>
        </FlexColumn>

        <div className="mx-auto flex h-full flex-1 items-center justify-center md:w-full">
          <GiveawayPlayButton
            text={videoButtonText ?? t('watchTrailerGiveaway', 'Watch Trailer')}
            onClick={handleOpenTrailer}
          />
        </div>

        <FlexColumn className="gap-6 md:hidden">
          {showInTheatersBadge && <InTheatersPill className="mx-auto" text={inTheatersText} />}

          <Button
            className="flex flex-col rounded-[14px] border-none !bg-giveaway-secondary-accent !py-[17px] md:mr-auto md:!px-7"
            onClick={onClick}
            variant="core-gray-900"
          >
            <GButtonMD color="white" className="flex items-center gap-2">
              {btnText}
            </GButtonMD>
          </Button>
        </FlexColumn>
      </div>

      <GiveawayVideoModal
        handleClose={handleCloseTrailer}
        showModal={isTrailerModalOpen}
        theatricalSlug={theatricalSlug}
        youTubeUrl={`https://www.youtube-nocookie.com/embed/${trailerId}?autoplay=1`}
      />
    </div>
  )
}

export default GiveawayHero

const InTheatersPill = ({ className, text }: { className?: string; text: string }) => {
  return (
    <GEyebrowMD
      className={classNames(
        'w-fit items-center justify-center rounded-md px-2 py-1 uppercase md:inline-flex bg-giveaway-primary-accent text-white',
        className,
      )}
    >
      {text}
    </GEyebrowMD>
  )
}
